<script setup lang="ts">
import MetadataInput from "@/components/diagnostics/MetadataInput.vue";
import { trackReportFeature } from "@/analytics/report-analytics";
import { trackDebounced } from "@/analytics/track-debounce";
import ReportContent from "../layout/ReportContent.vue";
import { computed, ref, watch } from "vue";
import ReportState, { ReportSection } from "@/stores/report/ReportState";
import { useGetReportTemplate } from "@/stores/queries/report-templates.query";
import type { Diagnostic, Report } from "@/types";
import { useAutoSave } from "@/composable/useAutoSave";
import { useUpdateReport } from "@/stores/queries";
import DRBtn from "@/components/UI/DRBtn.vue";

type DataPayload = {
  [key: string]: any;
};
const props = defineProps<{
  report: Report;
  diagnostic: Diagnostic;
}>();

const reportId = computed(() => props.report.id);

const { mutateAsync: updateReport } = useUpdateReport(reportId);

const reportTemplateId = computed(() => ReportState.activeTemplateId);
const { data: reportTemplate } = useGetReportTemplate(reportTemplateId);

const data = ref<DataPayload>({
  ...props.report.metadata,
});

// Vrai tant qu'un champ metadata a le focus : empêche le watch ci-dessous
// d'écraser la saisie en cours avec une valeur périmée renvoyée par le refetch.
const isEditingMetadata = ref(false);

const suggestions = computed(() => ({ ...props.diagnostic.suggestions }));

useAutoSave(data, (metadata: DataPayload) => {
  trackDebounced("report_metadata", () =>
    trackReportFeature("metadata_field_edited"),
  );
  updateReport({
    metadata,
  });
});

watch(
  () => props.report.metadata,
  (newMetadata) => {
    if (!newMetadata) return;
    if (isEditingMetadata.value) return;
    Object.keys(newMetadata).forEach((key) => {
      if (newMetadata[key] !== data.value[key]) {
        data.value[key] = newMetadata[key];
      }
    });
  },
  {
    immediate: true,
  },
);

const applySuggestions = () => {
  Object.keys(suggestions.value).forEach((key) => {
    data.value[key] = suggestions.value[key];
  });
};

const isSuggestions = computed(() => {
  return reportTemplate.value?.metadataDetectors?.some((metadataDetector) => {
    const key = metadataDetector.slug;
    return data.value[key] !== suggestions.value[key];
  });
});
</script>

<template>
  <ReportContent :title="$t('report:navigation.' + ReportSection.GENERAL)">
    <div v-if="reportTemplate" class="metadata-container w-100">
      <!-- Apply suggestions button -->
      <div v-if="isSuggestions" class="d-flex justify-content-end mb-3">
        <DRBtn
          variant="ia"
          :label="$t('report:report.metadata.apply.recommendations')"
          icon="bi-check2-all"
          @click="applySuggestions"
        />
      </div>

      <!-- Metadata Fields in white rounded card -->
      <div
        v-if="reportTemplate"
        class="metadata-card bg-white rounded-3 shadow-sm p-4"
        @focusin="isEditingMetadata = true"
        @focusout="isEditingMetadata = false"
      >
        <div class="row g-4">
          <div
            v-for="(
              metadataDetector, index
            ) in reportTemplate.metadataDetectors"
            :key="index"
            class="col-12 col-lg-6"
          >
            <MetadataInput
              v-model="data[metadataDetector.slug]"
              :suggestion="suggestions[metadataDetector.slug]"
              :metadataDetector="metadataDetector"
            />
          </div>
        </div>
      </div>
    </div>
  </ReportContent>
</template>

<style lang="scss" scoped>
.metadata-container {
  max-width: 900px;
}

.metadata-card {
  border: 1px solid var(--bs-border-color);
}
</style>
